/**
 * SolutionLibraryModal - Browse saved illustration templates
 * Matches Curex 10_cong_cu/thu_vien_giai_phap.png
 *
 * - Group saved solutions by month
 * - Each card shows: status badge, name, premium, date
 * - Actions: rename, duplicate, delete (via menu)
 */
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
} from 'react-native';
import { Icon } from './Icon';
import { Toast, useToast } from './Toast';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

export type SolutionStatus = 'scheduled' | 'saved' | 'shared';

export interface Solution {
  id: string;
  name: string;
  productId: string;
  premium: number;
  status: SolutionStatus;
  createdAt: string; // ISO date
}

export interface SolutionLibraryModalProps {
  visible: boolean;
  onClose: () => void;
  solutions: Solution[];
  onOpen: (id: string) => void;
  onRename?: (id: string, newName: string) => void;
  onDuplicate?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const STATUS_LABELS: Record<SolutionStatus, string> = {
  scheduled: 'ĐANG HẸN TH',
  saved: 'ĐÃ LƯU',
  shared: 'ĐÃ CHIA SẺ',
};

const STATUS_COLORS: Record<SolutionStatus, string> = {
  scheduled: '#1E40AF',
  saved: '#00A651',
  shared: '#7C3AED',
};

const formatNumber = (n: number): string =>
  n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/**
 * Group solutions by YYYY-MM month label
 */
function groupByMonth(solutions: Solution[]): Array<{ month: string; items: Solution[] }> {
  const groups: Record<string, Solution[]> = {};
  solutions.forEach((s) => {
    const d = new Date(s.createdAt);
    const key = `Tháng ${d.getMonth() + 1}, ${d.getFullYear()}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(s);
  });
  return Object.entries(groups).map(([month, items]) => ({ month, items }));
}

export const SolutionLibraryModal: React.FC<SolutionLibraryModalProps> = ({
  visible,
  onClose,
  solutions,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
}) => {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const { toastProps, showToast, hideToast } = useToast();

  if (!visible) return null;

  const handleRenameSubmit = (id: string) => {
    if (newName.trim()) {
      onRename?.(id, newName.trim());
      setRenamingId(null);
      setNewName('');
    }
  };

  const handleMenuAction = (id: string, action: 'rename' | 'duplicate' | 'delete') => {
    setOpenMenuId(null);
    if (action === 'rename') {
      setRenamingId(id);
      setNewName('');
    } else if (action === 'duplicate') {
      onDuplicate?.(id);
      showToast('Giải pháp đã được nhân bản', 'success');
    } else if (action === 'delete') {
      setConfirmDeleteId(id);
    }
  };

  const handleConfirmDelete = () => {
    if (confirmDeleteId) {
      onDelete?.(confirmDeleteId);
      showToast('Đã xóa giải pháp', 'success');
      setConfirmDeleteId(null);
    }
  };

  const groups = groupByMonth(solutions);

  return (
    <View style={styles.backdrop}>
      <TouchableOpacity
        style={StyleSheet.absoluteFill}
        activeOpacity={1}
        onPress={onClose}
      />
      <View style={styles.card}>
        {/* Header */}
        <View style={styles.header}>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>Thư viện giải pháp</Text>
            <View style={styles.savedBadge}>
              <Text style={styles.savedBadgeText}>Phiên bản đã lưu +{solutions.length}</Text>
            </View>
          </View>
          <TouchableOpacity onPress={onClose}>
            <Icon name="close" size={22} color={colors.onSurfaceVariant} />
          </TouchableOpacity>
        </View>

        {/* Body */}
        <ScrollView style={styles.body}>
          {groups.length === 0 ? (
            <View style={styles.emptyState}>
              <Icon name="description" size={48} color="#cbd5e1" />
              <Text style={styles.emptyTitle}>Chưa có giải pháp nào được lưu</Text>
              <Text style={styles.emptySubtitle}>
                Lưu giải pháp từ M07 để tái sử dụng cho nhiều khách hàng tương tự.
              </Text>
            </View>
          ) : (
            groups.map((group) => (
              <View key={group.month} style={styles.monthGroup}>
                <Text style={styles.monthHeader}>{group.month}</Text>
                {group.items.map((s) => {
                  const isRenaming = renamingId === s.id;
                  return (
                    <TouchableOpacity
                      key={s.id}
                      style={styles.solutionCard}
                      onPress={() => !isRenaming && onOpen(s.id)}
                    >
                      <View style={[styles.statusBadge, { backgroundColor: STATUS_COLORS[s.status] }]}>
                        <Text style={styles.statusBadgeText}>{STATUS_LABELS[s.status]}</Text>
                      </View>

                      {isRenaming ? (
                        <View style={styles.renameRow}>
                          <TextInput
                            style={styles.renameInput}
                            value={newName}
                            onChangeText={setNewName}
                            placeholder="Tên mới..."
                            autoFocus
                          />
                          <TouchableOpacity
                            onPress={() => handleRenameSubmit(s.id)}
                            style={styles.renameConfirm}
                          >
                            <Icon name="check" size={18} color="#ffffff" />
                          </TouchableOpacity>
                        </View>
                      ) : (
                        <View style={styles.solutionBody}>
                          <Text style={styles.solutionName}>{s.name}</Text>
                          <Text style={styles.solutionMeta}>
                            Phí bảo hiểm: {formatNumber(s.premium)} đ /năm
                          </Text>
                          <Text style={styles.solutionDate}>
                            {new Date(s.createdAt).toLocaleDateString('vi-VN')}
                          </Text>
                        </View>
                      )}

                      {/* Actions */}
                      {!isRenaming && (
                        <View style={{ position: 'relative' }}>
                          <TouchableOpacity
                            style={styles.menuBtn}
                            onPress={() => setOpenMenuId(openMenuId === s.id ? null : s.id)}
                            accessibilityLabel={`Tùy chọn cho ${s.name}`}
                          >
                            <Icon name="settings" size={20} color={colors.onSurfaceVariant} />
                          </TouchableOpacity>
                          {openMenuId === s.id && (
                            <View style={styles.actionMenu}>
                              <TouchableOpacity
                                style={styles.actionMenuItem}
                                onPress={() => handleMenuAction(s.id, 'rename')}
                              >
                                <Icon name="edit" size={14} color={colors.onSurface} />
                                <Text style={styles.actionMenuText}>Đổi tên gợi nhớ</Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={styles.actionMenuItem}
                                onPress={() => handleMenuAction(s.id, 'duplicate')}
                              >
                                <Icon name="content_copy" size={14} color={colors.onSurface} />
                                <Text style={styles.actionMenuText}>Sao chép</Text>
                              </TouchableOpacity>
                              <TouchableOpacity
                                style={[styles.actionMenuItem, styles.actionMenuItemDanger]}
                                onPress={() => handleMenuAction(s.id, 'delete')}
                              >
                                <Icon name="delete" size={14} color="#DC2626" />
                                <Text style={[styles.actionMenuText, { color: '#DC2626' }]}>Xóa</Text>
                              </TouchableOpacity>
                            </View>
                          )}
                        </View>
                      )}
                    </TouchableOpacity>
                  );
                })}
              </View>
            ))
          )}
        </ScrollView>

        {/* Confirm Delete Dialog */}
        {confirmDeleteId && (
          <View style={styles.confirmOverlay}>
            <View style={styles.confirmDialog}>
              <Text style={styles.confirmTitle}>Xác nhận xóa</Text>
              <Text style={styles.confirmMessage}>
                Bạn có chắc muốn xóa giải pháp này? Hành động này không thể hoàn tác.
              </Text>
              <View style={styles.confirmActions}>
                <TouchableOpacity
                  style={[styles.confirmBtn, styles.confirmBtnCancel]}
                  onPress={() => setConfirmDeleteId(null)}
                >
                  <Text style={styles.confirmBtnText}>Hủy</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.confirmBtn, styles.confirmBtnDanger]}
                  onPress={handleConfirmDelete}
                >
                  <Text style={[styles.confirmBtnText, { color: '#ffffff' }]}>Xóa</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}
      </View>

      {/* Toast — thay Alert.alert (no-op trên web) */}
      <Toast
        visible={toastProps.visible}
        message={toastProps.message}
        variant={toastProps.variant}
        onHide={hideToast}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
    zIndex: 1000,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    width: '100%',
    maxWidth: 540,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: spacing[4],
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
    gap: spacing[2],
  },
  title: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  savedBadge: {
    backgroundColor: '#FEF3C7',
    paddingHorizontal: spacing[2],
    paddingVertical: 4,
    borderRadius: 999,
    alignSelf: 'flex-start',
    marginTop: spacing[1],
  },
  savedBadgeText: {
    fontSize: 11,
    color: '#92400E',
    fontWeight: fontWeights.semibold,
  },
  body: {
    padding: spacing[3],
    maxHeight: 480,
  },
  emptyState: {
    alignItems: 'center',
    padding: spacing[8],
    gap: spacing[2],
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: fontWeights.semibold,
    color: colors.onSurfaceVariant,
  },
  emptySubtitle: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
    textAlign: 'center',
  },
  monthGroup: {
    marginBottom: spacing[3],
  },
  monthHeader: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[2],
  },
  solutionCard: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.md,
    padding: spacing[3],
    marginBottom: spacing[2],
    gap: spacing[2],
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: spacing[2],
    paddingVertical: 3,
    borderRadius: borderRadius.sm,
  },
  statusBadgeText: {
    fontSize: 10,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
    textTransform: 'uppercase',
  },
  solutionBody: {
    gap: 2,
  },
  solutionName: {
    fontSize: 14,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  solutionMeta: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  solutionDate: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    alignSelf: 'flex-end',
  },
  renameRow: {
    flexDirection: 'row',
    gap: spacing[2],
  },
  renameInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.md,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[2],
    fontSize: 13,
  },
  renameConfirm: {
    backgroundColor: colors.primary,
    width: 36,
    height: 36,
    borderRadius: borderRadius.md,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuBtn: {
    position: 'absolute',
    top: spacing[2],
    right: spacing[2],
    padding: 4,
  },
  actionMenu: {
    position: 'absolute',
    top: 32,
    right: 0,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    minWidth: 180,
    zIndex: 10,
  },
  actionMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[3],
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.outlineVariant,
  },
  actionMenuItemDanger: {
    backgroundColor: '#FEF2F2',
  },
  actionMenuText: {
    fontSize: 13,
    color: colors.onSurface,
  },

  // Confirm dialog
  confirmOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
    zIndex: 100,
  },
  confirmDialog: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[5],
    maxWidth: 400,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 12,
  },
  confirmTitle: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    marginBottom: spacing[2],
  },
  confirmMessage: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    lineHeight: 19,
    marginBottom: spacing[4],
  },
  confirmActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing[2],
  },
  confirmBtn: {
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2] + 2,
    borderRadius: borderRadius.md,
    borderWidth: 1,
  },
  confirmBtnCancel: {
    backgroundColor: '#ffffff',
    borderColor: colors.outline,
  },
  confirmBtnDanger: {
    backgroundColor: '#DC2626',
    borderColor: '#DC2626',
  },
  confirmBtnText: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
});